'use client'

import { useEffect, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form'
import { useSetToast } from '@/shared/hooks/useSetToast'
import { useCompany } from '@/shared/hooks/useCompanies'
import { useForm } from '@/shared/hooks/useForm'
import { resolveFileOnBrowser } from '@/shared/utils/filesUtils'
import Checkbox from '@/shared/v2/shared/components/Checkbox'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import ControlledCurrencySelect from '@/shared/v2/shared/components/ControlledCurrencySelect'
import { currencies, type Currency } from '@/shared/models/moneyModel'

import { BulkDebtorRowSchema } from '../services/bulkUploadServices'
import { BulkDialog } from './BulkUploadDialog'
import { useBulkCreateDebtors } from '../hooks/useBulkUpload'

const DebtorBulkUploadForm = z.object({
    file: z.instanceof(File),
    shouldCreateCreditLimit: z.boolean().optional().default(false),
    currency: z.enum(currencies)
})

type DebtorBulkUploadFormValues = z.infer<typeof DebtorBulkUploadForm>

const DebtorBulkUploadDialog = () => {
    const t = useTranslations('root.components.bulkUploadDialog')
    const toast = useSetToast()
    const debtorBulkUpload = useBulkCreateDebtors()
    const company = useCompany()

    const currencyConfigs = useMemo(() => company.data?.config.currencyConfigs ?? [], [company.data])
    const availableCurrencies: Currency[] = useMemo(() => currencyConfigs.map((c) => c.currency), [currencyConfigs])

    const [firstCurrency] = availableCurrencies

    const defaultValues: Partial<DebtorBulkUploadFormValues> = useMemo(
        () => ({
            shouldCreateCreditLimit: false,
            currency: firstCurrency
        }),
        [firstCurrency]
    )

    const methods = useForm({
        schema: DebtorBulkUploadForm,
        defaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange'
    })

    const {
        formState: { isDirty },
        watch,
        reset
    } = methods
    const shouldCreateCreditLimit = watch('shouldCreateCreditLimit')

    useEffect(() => {
        if (!isDirty) {
            reset(defaultValues)
        }
    }, [defaultValues, isDirty, reset])

    const onSubmit: SubmitHandler<DebtorBulkUploadFormValues> = async ({ file, shouldCreateCreditLimit, currency }) => {
        try {
            if (!shouldCreateCreditLimit || !currency) {
                await debtorBulkUpload.mutateAsync({
                    file,
                    options: { shouldCreateCreditLimit: false }
                })
            } else {
                await debtorBulkUpload.mutateAsync({
                    file,
                    options: { shouldCreateCreditLimit: true, currency }
                })
            }
        } catch {
            toast({
                intent: 'error',
                title: t('genericErrorTitle'),
                description: t('genericErrorDescription')
            })
        }
    }

    const onDownloadSuccessFile = () => {
        if (!debtorBulkUpload.data) return
        resolveFileOnBrowser(debtorBulkUpload.data, 'aria-bulk-result.csv', true)
    }

    const onReset = () => {
        debtorBulkUpload.reset()
        reset()
    }

    return (
        <BulkDialog.Root
            schema={BulkDebtorRowSchema}
            mutationStatus={debtorBulkUpload.status}
            canSubmit={methods.formState.isValid}
            onReset={onReset}
            onSubmit={methods.handleSubmit(onSubmit)}
            onDownloadSuccessFile={onDownloadSuccessFile}
            title={t('debtorTitle')}
            templateFileName="aria-debtors-template.csv"
            submitLabel={t('debtorSubmit')}
        >
            <FormProvider {...methods}>
                <Controller
                    name="file"
                    control={methods.control}
                    render={({ field }) => (
                        <BulkDialog.File
                            schema={BulkDebtorRowSchema}
                            value={field.value}
                            onChange={field.onChange}
                            isLoading={debtorBulkUpload.isPending}
                            isSuccess={debtorBulkUpload.status === 'success'}
                        />
                    )}
                />

                {(debtorBulkUpload.status === 'idle' || debtorBulkUpload.status === 'error') && (
                    <BulkDialog.Extra>
                        <Controller
                            name="shouldCreateCreditLimit"
                            control={methods.control}
                            render={({ field }) => (
                                <Checkbox
                                    data-testid="__bulk-upload-create-credit-limit-checkbox__"
                                    name={field.name}
                                    label={t('createCreditLimit')}
                                    checked={field.value}
                                    onCheckedChange={(value) => field.onChange(Boolean(value))}
                                />
                            )}
                        />

                        {shouldCreateCreditLimit &&
                            (availableCurrencies.length > 1 ? (
                                <ControlledCurrencySelect
                                    data-testid="__bulk-upload-currency-select__"
                                    label={t('currency')}
                                    placeholder={t('selectCurrencyPlaceholder')}
                                    name="currency"
                                    currencies={availableCurrencies}
                                    schema={DebtorBulkUploadForm}
                                />
                            ) : (
                                <Paragraph size={3} color="darkGrey" data-testid="__bulk-upload-currency-auto__">
                                    {t('currencyAutoApplied', { currency: availableCurrencies[0] })}
                                </Paragraph>
                            ))}
                    </BulkDialog.Extra>
                )}
            </FormProvider>
        </BulkDialog.Root>
    )
}

export default DebtorBulkUploadDialog
